import PageLayout from '@components/page/PageLayout'
import { Button, Checkbox, Flex, Table, Text, TextInput } from '@mantine/core'
import { useEffect, useState } from 'react'
import TableDeleteButton from '@components/button/TableDeleteButton'
import TableTemplate from '@components/table/TableTemplate'
import { ITHeadMap, ITable } from '@interfaces/ITable'
import { modals } from '@mantine/modals'
import { QueryObserverResult } from 'react-query'
import TablePagination from '@components/table/TablePagination'
import InputDate from '@components/inputs/InputDate'
import dayjs from '@config/dayjs'
import {
    IconCheck,
    IconExclamationCircle,
    IconTrash,
} from '@tabler/icons-react'
import api from '@config/api'
import { notifications } from '@mantine/notifications'
import Contact from '@api/Contact'
import {
    IResponseFindContactRowData,
    IResponseFindContact,
    IRequestDeleteContactByIdList,
} from '@apiinterface/IContact'
import InputTopicContact from '@components/inputs/quiz/InputTopicContact'
import DownloadExcel from '@components/button/DownloadExcel'
import DownloadContactExcel from '@components/button/DownloadContactExcel'

const ContactData = () => {
    const [selection, setSelection] = useState<number[]>([])
    const [limit, setlimit] = useState(10)
    const [orderby, setorderby] =
        useState<keyof IResponseFindContactRowData>('quiz_id')
    const [order, setorder] = useState<string>('desc')
    const [page, setpage] = useState(1)
    const [search, setsearch] = useState('')
    const [created_at, setcreated_at] = useState<string>('')
    const [topicContact, setTopicContact] = useState<string>('')

    const lists = Contact.useFindContact({
        limit,
        orderby,
        order,
        page,
        search,
        created_at,
        topic: topicContact,
    })

    useEffect(() => {
        lists.refetch()
        // eslint-disable-next-line
    }, [page, limit, order, orderby, topicContact, created_at])

    const openBulkDeleteConfirmModal = () => {
        const data: IRequestDeleteContactByIdList = {
            ids: selection,
        }
        const request = async () => {
            try {
                const res = await api.delete('/contact', { data })
                notifications.show({
                    title: res.data.message,
                    message: '',
                    color: 'teal',
                    icon: <IconCheck size='1rem' />,
                })
                lists.refetch()
                setSelection([])
            } catch (error: any) {
                notifications.show({
                    title: 'เกิดข้อผิดพลาด',
                    message: error.response?.data.message,
                    color: 'red',
                    icon: <IconExclamationCircle size='1rem' />,
                })
                setSelection([])
            }
        }
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: <Text>ลบรายการ {selection.length} รายการ</Text>,
            onConfirm: () => {
                request()
            },
            modalId: 'deleteBulk',
        })
    }

    const thead: ITHeadMap<IResponseFindContactRowData>[] = [
        {
            sortkey: 'id',
            label: 'ไอดี',
            width: '5%',
        },
        {
            sortkey: 'quiz_id',
            label: 'quiz_id',
            width: '5%',
        },
        {
            sortkey: 'topic',
            label: 'เรื่องที่ต้องต้องการปรึกษา',
            width: '15%',
        },
        {
            sortkey: 'firstname',
            label: 'ชื่อ',
            width: '10%',
        },
        {
            sortkey: 'lastname',
            label: 'นามสกุล',
            width: '10%',
        },
        {
            sortkey: 'email',
            label: 'อีเมล',
            width: '10%',
        },
        {
            sortkey: 'mobileno',
            label: 'เบอร์โทรศัพท์',
            width: '10%',
        },
        {
            sortkey: 'disclosing',
            label: 'เปิดเผยข้อมูล',
            width: '5%',
        },
        {
            sortkey: 'created_at',
            label: 'สร้างเมื่อ',
            width: '10%',
        },
        {
            label: 'Action',
            width: '5%',
        },
    ]

    return (
        <PageLayout pageTitle='ข้อมูลให้ที่ปรึกษาทางการเงินติดต่อกลับ'>
            <Flex gap='md' direction={{ xs: 'column', lg: 'row' }}>
                <TextInput
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            console.log('Enter')
                            lists.refetch()
                        }
                    }}
                    w='100%'
                    value={search}
                    onChange={(event) => setsearch(event.target.value)}
                    placeholder='ค้นหา'
                    label='ค้นหารายการ'
                />
                <InputTopicContact
                    withAll
                    miw='180px'
                    w='100%'
                    value={String(topicContact)}
                    onChange={(value) => {
                        if (value === null) {
                            setTopicContact(String(''))
                            setpage(1)
                        } else if (value && value !== String(topicContact)) {
                            setTopicContact(String(value))
                            setpage(1)
                        } else {
                            setTopicContact(String(''))
                            setpage(1)
                        }
                    }}
                />
                <InputDate
                    clearable
                    miw='180px'
                    label='สร้างเมื่อ'
                    placeholder='วันที่'
                    value={created_at! as any}
                    onChange={(value) => {
                        if (value !== (created_at as any)) {
                            setcreated_at(value as any)
                            setpage(1)
                        }
                    }}
                />
                <DownloadContactExcel
                    data={lists?.data?.lists as IResponseFindContactRowData[]}
                />
            </Flex>
            {selection.length ? (
                <Flex
                    mt='lg'
                    py='xs'
                    px='md'
                    align='center'
                    justify='space-between'
                    style={{ borderRadius: '10px' }}
                    bg='blue'
                >
                    <Text c='text.0'>เลือก {selection.length} รายการ</Text>
                    <Button
                        py={0}
                        onClick={openBulkDeleteConfirmModal}
                        leftSection={<IconTrash size={14} />}
                        variant='filled'
                    >
                        ลบ
                    </Button>
                </Flex>
            ) : (
                ''
            )}
            <TableTemplate<IResponseFindContactRowData>
                thead={thead}
                KeyID={'quiz_id'}
                orderby={orderby}
                order={order}
                setorder={setorder}
                setorderby={setorderby}
                lists={lists?.data?.lists}
                setSelection={setSelection}
                selection={selection}
            >
                {lists?.data?.lists.map((value) => {
                    return (
                        <AllDataTableRow
                            selection={selection}
                            setSelection={setSelection}
                            key={value.id}
                            data={value}
                            refreshTable={lists.refetch}
                        />
                    )
                })}
            </TableTemplate>
            {lists.data?.lists.length ? (
                <TablePagination
                    limit={lists.data?.lists.length}
                    setlimit={setlimit}
                    setpage={setpage}
                    page={page}
                    totalPages={lists.data.totalPages}
                    totalRows={lists.data.totalRows}
                />
            ) : (
                <></>
            )}
        </PageLayout>
    )
}

const AllDataTableRow = ({
    data,
    refreshTable,
    selection,
    setSelection,
}: {
    data: IResponseFindContactRowData
    selection: ITable<IResponseFindContactRowData>['selection']
    setSelection: ITable<IResponseFindContactRowData>['setSelection']
    refreshTable: () => Promise<
        QueryObserverResult<IResponseFindContact, unknown>
    >
}) => {
    const reqDeleteProjectVendor = Contact.useDeleteContactById({
        id: data.id as number,
        aftersuccess: () => {
            refreshTable()
            setSelection([])
        },
    })
    const toggleRow = (id: number) =>
        setSelection((current) =>
            current.includes(id)
                ? current.filter((item) => item !== id)
                : [...current, id]
        )
    const openDeleteConfirmModal = () =>
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: <Text>ลบรายการข้อมูลไอดี {data.id}</Text>,
            onConfirm: reqDeleteProjectVendor.onSubmit,
        })

    return (
        <tr key={data.id}>
            <Table.Td>
                <Checkbox
                    checked={selection.includes(data.id)}
                    onChange={() => toggleRow(data.id)}
                />
            </Table.Td>
            <td>{data.id}</td>
            <td>{data.quiz_id}</td>
            <td>{data.topic}</td>
            <td>{data.firstname}</td>
            <td>{data.lastname}</td>
            <td>{data.email}</td>
            <td>{data.mobileno}</td>
            <Checkbox checked={data.disclosing === 'T'} />
            <td> {dayjs(data.created_at).fromNow()}</td>
            <td>
                <Flex gap='xs' pos='relative'>
                    <TableDeleteButton onClick={openDeleteConfirmModal} />
                </Flex>
            </td>
        </tr>
    )
}

export default ContactData
